import React, { useState, useEffect } from 'react';
import { Users, Check, Phone, MessageCircle, Car, Heart, ChevronLeft, ChevronRight, Shield, Clock, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FleetPage = () => {
  const { settings } = useSiteSettings();
  const contactInfo = settings?.contact_info || {};
  const fleetPartnerLink = settings?.fleet_partner?.website_url || 'https://www.example.com';
  const [fleetData, setFleetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [likedVehicles, setLikedVehicles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', pickupLocation: '', dropLocation: '', pickupDate: '', returnDate: '', message: '' });

  useEffect(() => {
    const fetchFleet = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fleet`);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setFleetData(data);
      } catch (e) {
        console.error('Error fetching fleet:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchFleet();
  }, []);

  const vehicleTypes = ['all', 'Sedan', 'SUV', 'Luxury Sedan', 'Premium SUV', 'Mini Bus', 'Bus'];
  const filteredFleets = fleetData.filter(fleet => selectedType === 'all' || fleet.type === selectedType);
  const toggleLike = (id) => setLikedVehicles(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  const handleEnquiry = (vehicle) => { setSelectedVehicle(vehicle); setShowEnquiryDialog(true); };
  
  // Gallery functions
  const openGallery = (vehicle) => {
    const images = vehicle.images?.length > 0 ? [vehicle.image, ...vehicle.images] : [vehicle.image];
    setGalleryImages(images.filter(Boolean));
    setCurrentGalleryIndex(0);
    setSelectedVehicle(vehicle);
    setShowGalleryDialog(true);
  };
  const nextGalleryImage = () => setCurrentGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  const prevGalleryImage = () => setCurrentGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  
  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: enquiryForm.name,
          email: enquiryForm.email,
          phone: enquiryForm.phone,
          message: `Vehicle: ${selectedVehicle?.name} | Pickup: ${enquiryForm.pickupLocation} | Drop: ${enquiryForm.dropLocation} | Dates: ${enquiryForm.pickupDate} to ${enquiryForm.returnDate} | ${enquiryForm.message}`,
          enquiry_type: 'fleet',
          fleet_id: selectedVehicle?.id
        })
      });
      if (!res.ok) throw new Error('Failed to submit');
      alert(`Enquiry submitted for ${selectedVehicle?.name}!`);
      setShowEnquiryDialog(false);
      setEnquiryForm({ name: '', email: '', phone: '', pickupLocation: '', dropLocation: '', pickupDate: '', returnDate: '', message: '' });
    } catch (err) {
      alert('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const handleWhatsAppEnquiry = (vehicle) => { window.open(`https://wa.me/${(contactInfo.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'm interested in renting ${vehicle.name} (${vehicle.type}).`)}`, '_blank'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-s-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-s-primary animate-spin mx-auto mb-4" />
          <p className="text-s-text2">Loading fleet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-s-bg">
      <SEO 
        title="Vehicle Fleet Rental | Cars, SUVs, Tempo Travellers - Sentora Holidays" 
        description="Book premium vehicles for your India tour. Wide range of sedans, SUVs, luxury cars, and tempo travellers. 24/7 support, fully insured, experienced drivers."
        keywords="car rental India, SUV hire, tempo traveller booking, travel vehicle rental, tour transport, India vehicle hire"
        url="https://www.sentoraholidays.com/fleet"
      />
      
      {/* Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1601929862217-f1bf94503333?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-s-bg via-s-bg/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-s-bg via-transparent to-s-bg/50" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <Badge className="bg-s-primary text-white border-0 mb-3 md:mb-4 text-xs badge-pop"><Car size={12} className="mr-1" /> PREMIUM FLEET</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-s-text mb-3 md:mb-4 font-heritage italic hero-text-reveal">Our Fleet</h1>
            <p className="text-sm md:text-lg lg:text-xl text-s-text2 mb-4 md:mb-6 hero-text-reveal-delay">Wide range of vehicles for every journey</p>
            <div className="flex gap-4 md:gap-8 hero-text-reveal-delay">
              {[{ icon: Car, label: 'Vehicles', value: '50+' }, { icon: Shield, label: 'Insured', value: '100%' }, { icon: Clock, label: 'Support', value: '24/7' }].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-white/10 rounded-lg flex items-center justify-center"><stat.icon className="w-4 h-4 md:w-5 md:h-5 text-s-primary" /></div>
                  <div><p className="text-s-text font-bold text-sm md:text-base">{stat.value}</p><p className="text-s-text2 text-[10px] md:text-sm">{stat.label}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-20 z-30 bg-s-bg/95 backdrop-blur-md border-b border-s-border py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {vehicleTypes.map((type) => (
                <Button key={type} variant={selectedType === type ? 'default' : 'outline'} onClick={() => setSelectedType(type)} size="sm"
                  className={`rounded-full whitespace-nowrap ${selectedType === type ? 'bg-s-primary text-white hover:bg-s-primary/90' : 'border-s-border text-s-text2 hover:bg-s-card'}`}>
                  {type === 'all' ? 'All Vehicles' : type}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredFleets.map((vehicle, idx) => (
            <AnimatedCard key={vehicle.id} delay={idx * 80} hoverEffect="none" className="group" onMouseEnter={() => setHoveredCard(vehicle.id)} onMouseLeave={() => setHoveredCard(null)}>
              <div className={`relative rounded-2xl overflow-hidden bg-s-bg2 transition-all duration-500 ${hoveredCard === vehicle.id ? 'scale-105 shadow-2xl shadow-s-primary/20 z-10' : ''}`}>
                <div className="relative h-56 overflow-hidden cursor-pointer" onClick={() => openGallery(vehicle)}>
                  <img loading="lazy" src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-s-bg2 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className="bg-s-primary text-white">{vehicle.type}</Badge>
                    {vehicle.available && <Badge className="bg-s-success text-white">Available</Badge>}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); toggleLike(vehicle.id); }} className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 p-2 rounded-full transition-all hover:scale-110">
                    <Heart size={20} className={likedVehicles.includes(vehicle.id) ? 'fill-[#7A3040] text-[#7A3040]' : 'text-white'} />
                  </button>
                  {/* Gallery indicator */}
                  {vehicle.images?.length > 0 && (
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                      <span>{vehicle.images.length + 1} photos</span>
                    </div>
                  )}
                  <div className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity ${hoveredCard === vehicle.id ? 'opacity-100' : 'opacity-0'}`}>
                    <span className="bg-white/90 text-black px-4 py-2 rounded-full text-sm font-medium">View Gallery</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div><h3 className="text-xl font-bold text-s-text">{vehicle.name}</h3><p className="text-s-text2 text-sm flex items-center gap-1"><Users size={14} /> {vehicle.capacity}</p></div>
                    <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30">{vehicle.type || 'Premium'}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {vehicle.features.map((feature, idx) => (<span key={idx} className="text-xs bg-s-card text-s-text2 px-2 py-1 rounded-full flex items-center gap-1"><Check size={10} className="text-s-success" />{feature}</span>))}
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-s-border">
                    <Button className="flex-1 bg-s-primary hover:bg-s-primary-h" onClick={() => handleEnquiry(vehicle)}><MessageCircle size={16} className="mr-1" />Enquire</Button>
                    <Button className="bg-s-primary hover:bg-s-primary px-4" onClick={() => handleWhatsAppEnquiry(vehicle)}>
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </Button>
                  </div>
                </div>
              </div>
            </AnimatedCard>
          ))}
        </div>
      </section>

      {/* Gallery Modal */}
      <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
        <DialogContent className="max-w-4xl bg-black/95 border-none p-0">
          <button onClick={() => setShowGalleryDialog(false)} className="absolute top-4 right-4 z-50 bg-white/20 hover:bg-white/30 p-2 rounded-full">
            <X className="text-white" size={24} />
          </button>
          
          {galleryImages.length > 0 && (
            <div className="relative">
              {/* Main Image */}
              <div className="relative h-[60vh] flex items-center justify-center">
                <img 
                  src={galleryImages[currentGalleryIndex]} 
                  alt={`${selectedVehicle?.name} - Image ${currentGalleryIndex + 1}`} 
                  className="max-h-full max-w-full object-contain"
                />
                
                {/* Navigation Arrows */}
                {galleryImages.length > 1 && (
                  <>
                    <button onClick={prevGalleryImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full transition-colors">
                      <ChevronLeft className="text-white" size={28} />
                    </button>
                    <button onClick={nextGalleryImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full transition-colors">
                      <ChevronRight className="text-white" size={28} />
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail Strip */}
              {galleryImages.length > 1 && (
                <div className="flex justify-center gap-2 p-4 bg-black/50">
                  {galleryImages.map((img, idx) => (
                    <button 
                      key={idx} 
                      onClick={() => setCurrentGalleryIndex(idx)}
                      className={`w-16 h-12 rounded overflow-hidden border-2 transition-all ${idx === currentGalleryIndex ? 'border-s-primary scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img loading="lazy" src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Image Counter */}
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                {currentGalleryIndex + 1} / {galleryImages.length}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Dialog */}
      <Dialog open={showEnquiryDialog} onOpenChange={setShowEnquiryDialog}>
        <DialogContent className="sm:max-w-md bg-s-bg2 border-s-border text-s-text">
          <DialogHeader><DialogTitle className="text-xl">Vehicle Enquiry</DialogTitle></DialogHeader>
          {selectedVehicle && (
            <div className="bg-s-card rounded-lg p-4 mb-4 flex items-center gap-4">
              <img loading="lazy" src={selectedVehicle.image} alt="" className="w-20 h-20 rounded-lg object-cover" />
              <div><p className="font-semibold text-s-text">{selectedVehicle.name}</p><p className="text-sm text-s-text2">{selectedVehicle.type} • {selectedVehicle.capacity}</p></div>
            </div>
          )}
          <form onSubmit={handleEnquirySubmit} className="space-y-4">
            <div><Label className="text-s-text2">Full Name *</Label><Input value={enquiryForm.name} onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})} className="bg-s-card border-s-border text-s-text" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-s-text2">Email *</Label><Input type="email" value={enquiryForm.email} onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})} className="bg-s-card border-s-border text-s-text" required /></div>
              <div><Label className="text-s-text2">Phone *</Label><Input value={enquiryForm.phone} onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})} className="bg-s-card border-s-border text-s-text" required /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-s-text2">Pickup Location</Label><Input value={enquiryForm.pickupLocation} onChange={(e) => setEnquiryForm({...enquiryForm, pickupLocation: e.target.value})} className="bg-s-card border-s-border text-s-text" /></div>
              <div><Label className="text-s-text2">Drop Location</Label><Input value={enquiryForm.dropLocation} onChange={(e) => setEnquiryForm({...enquiryForm, dropLocation: e.target.value})} className="bg-s-card border-s-border text-s-text" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-s-text2">Pickup Date</Label><Input type="date" value={enquiryForm.pickupDate} onChange={(e) => setEnquiryForm({...enquiryForm, pickupDate: e.target.value})} className="bg-s-card border-s-border text-s-text" /></div>
              <div><Label className="text-s-text2">Return Date</Label><Input type="date" value={enquiryForm.returnDate} onChange={(e) => setEnquiryForm({...enquiryForm, returnDate: e.target.value})} className="bg-s-card border-s-border text-s-text" /></div>
            </div>
            <div><Label className="text-s-text2">Additional Requirements</Label><Textarea placeholder="Any special requirements..." value={enquiryForm.message} onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})} className="bg-s-card border-s-border text-s-text" rows={3} /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-s-primary hover:bg-s-primary-h">{submitting ? 'Submitting...' : 'Submit Enquiry'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FleetPage;
